import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function InteriorDetailingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Interior Car Detailing',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Oxnard', 'Ventura', 'Thousand Oaks'],
    description: 'Professional mobile interior car detailing services including deep cleaning, shampooing, steam sanitizing, and leather conditioning.'
  };
  const faqs = [{
    question: 'What does interior detailing include?',
    answer: 'Our interior detail includes deep vacuuming, shampooing of carpets and fabric seats, leather cleaning and conditioning, steam sanitizing of vents and high-touch surfaces, plastic and vinyl restoration, and streak-free interior glass.'
  }, {
    question: 'How long does interior detailing take?',
    answer: 'Interior detailing typically takes 2\u20133 hours depending on the size and condition of your vehicle. Heavily soiled interiors or pet hair removal may take longer.'
  }, {
    question: 'Can you remove stains and odors?',
    answer: 'Yes. Our shampooing and steam extraction lift most deep-set stains, and our sanitizing process neutralizes odors from spills, smoke, and pets.'
  }, {
    question: 'How much does interior detailing cost in Ventura County?',
    answer: 'Interior detailing starts at $180, with pricing adjusted for vehicle size and condition. Call (818) 381-9905 for an exact quote.'
  }, {
    question: 'Do you come to my home or office?',
    answer: 'Yes — we are fully mobile, bringing our own water and detailing equipment to your home or office anywhere in Ventura County. We require access to a standard electrical outlet at the service location.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Interior Detailing',
    item: 'https://www.fulldetailpro.com/services/interior-detailing'
  }];
  return <ServicePageLayout title="Interior Detailing Ventura County" subtitle="Restore your vehicle's cabin to a factory-fresh feel with our comprehensive mobile interior detailing service. We come directly to your home or office." metaDescription="Expert mobile interior detailing in Ventura County \u2014 Camarillo, Oxnard, Ventura & Thousand Oaks. Deep cleaning, stain removal & steam sanitizing. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-1122" data-mp="h5z1q806kd8a">
      <p data-id="element-1123" data-mp="3pl8wsw5nki6k">
        Your vehicle's interior is where you spend all your time driving. Over
        time, dirt, dust, spills, pet hair, and odors can accumulate, making
        your daily commute less enjoyable and potentially impacting your health.
        At Mike's Pro Detail, our{' '}
        <strong data-id="element-1124" data-mp="1cfccs32be1lp">mobile interior car detailing</strong>{' '}
        service is designed to deep clean, sanitize, and restore your cabin to a
        like-new condition. Whether you're in Camarillo CA, Thousand Oaks, or
        anywhere in Ventura County, our fully equipped mobile unit brings
        premium interior detailing directly to your doorstep.
      </p>

      <h2 data-id="element-1125" data-mp="17ic55nptgqsh">What Our Interior Detailing Includes</h2>
      <p data-id="element-1126" data-mp="1v74uv2fui2en">
        We don't just wipe down surfaces; we perform a meticulous, multi-step
        deep cleaning process to ensure every crack and crevice is addressed.
        Our comprehensive interior detail includes:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-1127" data-mp="1lg0h261wonle">
        <li className="flex items-start" data-id="element-1128" data-mp="1qelha5h5r79u">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1129" data-mp="1qdk7037yuot6" />
          <span data-id="element-1130" data-mp="2e3wifo2f73oi">
            <strong data-id="element-1131" data-mp="2e3ej2ikgo9cd">Thorough Vacuuming:</strong> Deep extraction of dirt,
            debris, and pet hair from carpets, seats, floor mats, and the trunk
            area.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1132" data-mp="v8xkz0g9dtn2">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1133" data-mp="q8jbrf2x8dng" />
          <span data-id="element-1134" data-mp="qackczx3h7y3">
            Cleaning of fabric seats, carpets, and floor mats to lift deep-set
            stains and grime.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1135" data-mp="gavd4e272m00">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1136" data-mp="gc6l6q2oafwx" />
          <span data-id="element-1137" data-mp="1dwj3ah824833">
            <strong data-id="element-1138" data-mp="1dx2ivra8ikdy">Leather Cleaning & Conditioning:</strong> Safe, pH-balanced
            cleaning of leather seats followed by a premium conditioner to
            prevent cracking and fading.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1139" data-mp="145ohfdyq3ai6">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1140" data-mp="z7wocgmcdice" />
          <span data-id="element-1141" data-mp="1wo24qjmfne30">
            <strong data-id="element-1142" data-mp="1wollvy49dyfz">Steam Cleaning & Sanitization:</strong> High-pressure steam
            is used on vents, cup holders, and high-touch areas to kill bacteria
            and melt away sticky residue.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1143" data-mp="207u7vh7oublu">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1144" data-mp="247usxkpall1v" />
          <span data-id="element-1145" data-mp="249xjq6y9ri7n">
            <strong data-id="element-1146" data-mp="249o1x6ndohd1">Plastics & Vinyl Restoration:</strong> Cleaning and UV
            protection applied to the dashboard, door panels, and center console
            to restore a rich, non-greasy finish.
          </span>
        </li>
        <li className="flex items-start" data-id="element-1147" data-mp="2j5x1yaf3eyqi">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-1148" data-mp="2j4vf78opsnsy" />
          <span data-id="element-1149" data-mp="2n5nq06d8j0rc">
            <strong data-id="element-1150" data-mp="2n55rf333oag9">Streak-Free Windows:</strong> Interior glass and mirrors are
            cleaned to a crystal-clear, streak-free shine for optimal
            visibility.
          </span>
        </li>
      </ul>

      <h2 data-id="element-1151" data-mp="2wymnkdqpm1n3">The Benefits of Professional Interior Detailing</h2>
      <p data-id="element-1152" data-mp="31wo5gvcefuwz">
        Investing in a professional interior detail goes beyond just aesthetics.
        A clean interior significantly improves the air quality inside your
        vehicle by removing dust mites, allergens, and bacteria trapped in the
        upholstery and ventilation system. Furthermore, regular conditioning of
        leather and UV protection on plastics prevents premature aging,
        cracking, and fading caused by the intense Southern California sun. If
        you're planning to sell your vehicle, a pristine interior is one of the
        most effective ways to maximize its resale value.
      </p>

      <h2 data-id="element-1153" data-mp="2zv7dp05yb17q">Why Choose Mike's Pro Detail for Interior Detailing?</h2>
      <p data-id="element-1154" data-mp="2uwxgyl4kvsk0">
        When searching for "Mobile Auto Detailing Camarillo CA" or "Car
        Detailing Camarillo", you want a service that is reliable, meticulous,
        and convenient. Mike's Pro Detail stands out because we use only
        premium, eco-friendly products and professional-grade equipment steam
        cleaners. We understand that your time is valuable, which is why our
        mobile detailing Ventura County service comes entirely to you—fully
        equipped with our own water and detailing equipment. We require access
        to a standard electrical outlet at the service location. From Camarillo
        and Oxnard to Thousand Oaks, Simi Valley, and Westlake Village, we
        deliver 5-star results without you ever having to leave your home or
        office.
      </p>

      <h2 data-id="element-1155" data-mp="3hdpepfluo2fz">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-1156" data-mp="3hco0j29jezu1">
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1157" data-mp="33den2acpujtr">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1158" data-mp="2yfv97bjxk92c">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1159" data-mp="wo0115wvumov">
          Mobile Detailing &rarr;
        </Link>
        <Link to="/services/headlight-restoration" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-1160" data-mp="36uhg41mmg9bt">
          Headlight Restoration &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}